/**
 * Dataset da gestão centralizada de pedidos (Task 12, brief 3.3).
 *
 * Uma fonte (`/api/platform/orders`) com o seu estado declarado, no mesmo
 * vocabulário do dashboard e do catálogo (`live` vs `awaiting_contract`):
 * fonte ausente é dito, nunca convertido em "nenhum pedido".
 *
 * Os seletores aqui são a fonte ÚNICA de "quais pedidos compõem este recorte":
 * a lista, o detalhe e o drill-down do indicador leem daqui, então não existe
 * contagem de tela discordando da contagem do card. Puro e sem I/O.
 */
import { ORDER_ORIGINS, ORDER_ORIGIN_LABELS, nextStates, requirementsFor, type OrderTransitionField, type PlatformOrderOrigin, type PlatformOrderState } from "./state-machine.js";
import type { DashboardSourceState } from "../dashboard/kpis.js";
import type { PlatformOrderEvent, PlatformUnifiedOrder } from "../api/types.js";

export interface OrdersSources {
  platformOrders: DashboardSourceState;
}

export interface OrdersDataset {
  orders: PlatformUnifiedOrder[];
  sources: OrdersSources;
}

/** Estado inicial honesto: nada buscado ainda (não é "nenhum pedido"). */
export function emptyOrdersDataset(): OrdersDataset {
  return { orders: [], sources: { platformOrders: "awaiting_contract" } };
}

export function isOrdersSourceAwaitingContract(dataset: OrdersDataset): boolean {
  return dataset.sources.platformOrders === "awaiting_contract";
}

export function unifiedOrderById(dataset: OrdersDataset, orderId: string): PlatformUnifiedOrder | null {
  return dataset.orders.find((order) => order.id === orderId) ?? null;
}

/**
 * Pedidos unificados que projetam um conjunto de pedidos do CHECKOUT.
 *
 * É a ponte do drill-down: os indicadores do dashboard (Task 10) contam
 * registros de `/api/checkout/orders`, e esta função traduz aqueles ids para as
 * linhas da lista unificada — sem recalcular o filtro do indicador e sem que a
 * lista invente um recorte próprio. `null` = sem recorte (lista completa).
 */
export function ordersForCheckoutIds(dataset: OrdersDataset, checkoutOrderIds: string[] | null): PlatformUnifiedOrder[] {
  if (checkoutOrderIds === null) return dataset.orders;
  const wanted = new Set(checkoutOrderIds);
  return dataset.orders.filter((order) => order.checkoutOrderId !== null && wanted.has(order.checkoutOrderId));
}

/**
 * Pedidos que NÃO vêm do checkout da Loja (WhatsApp, marketplace, manual).
 *
 * Usado para declarar, na tela filtrada por indicador, quantos pedidos de
 * outros canais ficam FORA daquele indicador — um número omitido em silêncio é
 * a forma mais fácil de o operador concluir que a Loja é a operação inteira.
 */
export function ordersOutsideCheckout(dataset: OrdersDataset): PlatformUnifiedOrder[] {
  return dataset.orders.filter((order) => order.checkoutOrderId === null);
}

export function ordersByOrigin(dataset: OrdersDataset, origin: PlatformOrderOrigin): PlatformUnifiedOrder[] {
  return dataset.orders.filter((order) => order.origin === origin);
}

export interface OriginBreakdownRow {
  origin: PlatformOrderOrigin;
  label: string;
  count: number;
}

/** Composição da lista por canal de origem, em ordem estável. */
export function originBreakdown(dataset: OrdersDataset): OriginBreakdownRow[] {
  return ORDER_ORIGINS.map((origin) => ({
    origin,
    label: ORDER_ORIGIN_LABELS[origin],
    count: ordersByOrigin(dataset, origin).length
  }));
}

/** Contagem por estado da máquina (fila operacional da tela de pedidos). */
export function stateBreakdown(dataset: OrdersDataset): Map<PlatformOrderState, number> {
  const counts = new Map<PlatformOrderState, number>();
  for (const order of dataset.orders) {
    counts.set(order.state, (counts.get(order.state) ?? 0) + 1);
  }
  return counts;
}

/**
 * Linha do tempo do pedido: do evento mais antigo ao mais recente.
 *
 * Ordenação estável por `occurredAt` e, em empate, pela ordem em que o servidor
 * publicou — duas transições no mesmo milissegundo (criação + espelho do
 * pagamento) não podem trocar de lugar entre renderizações.
 */
export function orderTimeline(order: PlatformUnifiedOrder): PlatformOrderEvent[] {
  return order.events
    .map((event, index) => ({ event, index }))
    .sort((a, b) => a.event.occurredAt.localeCompare(b.event.occurredAt) || a.index - b.index)
    .map((entry) => entry.event);
}

/** Último evento registrado; `null` quando o pedido ainda não tem histórico. */
export function lastOrderEvent(order: PlatformUnifiedOrder): PlatformOrderEvent | null {
  return orderTimeline(order).at(-1) ?? null;
}

export interface OriginDrillDown {
  /** Rota do SPA que abre o registro de origem; `null` quando é externo. */
  route: string | null;
  label: string;
  /** Identificador exibido (id da conversa, do pedido do checkout, do canal). */
  reference: string;
}

/**
 * Para onde navegar para ver a ORIGEM do pedido (D-03 aplicado ao brief 3.3).
 *
 * Origem interna leva ao registro dentro da Plataforma (conversa do
 * atendimento, pedido do checkout). Origem externa (marketplace) não tem tela
 * aqui: devolvemos `route: null` com o identificador do canal visível, em vez
 * de um link que não leva a lugar algum.
 */
export function originDrillDown(order: PlatformUnifiedOrder): OriginDrillDown {
  if (order.conversationId) {
    return {
      route: `/inbox/${encodeURIComponent(order.conversationId)}`,
      label: "Abrir a conversa de origem",
      reference: order.conversationId
    };
  }
  if (order.checkoutOrderId) {
    return {
      // O recorte `?checkout=` abre a lista unificada já posicionada no pedido
      // do checkout que originou a linha — mesmo registro, mesma lista.
      route: `/pedidos?checkout=${encodeURIComponent(order.checkoutOrderId)}`,
      label: "Ver o pedido do checkout da Loja que originou este registro",
      reference: order.checkoutOrderId
    };
  }
  return {
    route: null,
    label: `Origem externa (${ORDER_ORIGIN_LABELS[order.origin]}) — identificador preservado`,
    reference: order.externalId
  };
}

export interface AvailableTransition {
  to: PlatformOrderState;
  requires: readonly OrderTransitionField[];
}

/**
 * Próximos passos que a tela pode oferecer, com o que cada um exige. Vem da
 * máquina de estados pura — a tela não mantém a sua própria lista de botões.
 */
export function availableTransitions(order: PlatformUnifiedOrder): AvailableTransition[] {
  return nextStates(order.state).map((to) => ({ to, requires: requirementsFor(to) }));
}
